import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiInstance } from '../../api/apiInstance';
import { RootState } from '../../store/store';
import { EditCompanyInfoDto, PafValues } from './constants';

export const getCompanyInfo = createAsyncThunk('company/getCompanyInfo', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.get('/organization');

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});

export const getCompanyMembers = createAsyncThunk('company/getCompanyMembers', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.get('/organization/members');

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});

export const acceptInvitation = createAsyncThunk(
  'company/acceptInvitation',
  async (memberId: string, { rejectWithValue }) => {
    try {
      await apiInstance.post('/organization/members/approve', {
        memberId,
      });

      return memberId;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const denyInvitation = createAsyncThunk(
  'company/denyInvitation',
  async (memberId: string, { rejectWithValue }) => {
    try {
      await apiInstance.post('/organization/members/deny', {
        memberId,
      });

      return memberId;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const inviteMember = createAsyncThunk('company/inviteMember', async (_, { rejectWithValue, getState }) => {
  try {
    const state = getState() as RootState;
    const newUser = state.company.newUserForm.value;

    const response = await apiInstance.post('/organization/members/invite', {
      email: newUser,
    });

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});

export const reinviteMember = createAsyncThunk(
  'company/reinviteMember',
  async (email: string, { rejectWithValue, getState }) => {
    try {
      const response = await apiInstance.post('/organization/members/reinvite', {
        email,
      });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const promoteMember = createAsyncThunk(
  'company/promoteMember',
  async (payload: { memberId: string; role: string }, { rejectWithValue }) => {
    try {
      await apiInstance.post('/organization/members/promote', {
        memberId: payload.memberId,
      });

      return { memberId: Number(payload.memberId), role: payload.role };
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const demoteMember = createAsyncThunk(
  'company/demoteMember',
  async (payload: { memberId: string; role: string }, { rejectWithValue }) => {
    try {
      await apiInstance.post('/organization/members/demote', {
        memberId: payload.memberId,
      });

      return { memberId: Number(payload.memberId), role: payload.role };
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const deactivateMember = createAsyncThunk(
  'company/deactivateMember',
  async (memberId: string, { rejectWithValue }) => {
    try {
      const response = await apiInstance.post('/organization/members/deactivate', {
        memberId,
      });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const editCompanyInfo = createAsyncThunk(
  'company/editCompanyInfo',
  async (companyInfo: EditCompanyInfoDto, { rejectWithValue }) => {
    try {
      const response = await apiInstance.patch('/organization', {
        ...companyInfo,
      });

      return response.data;
    } catch (error: any) {
      const errorStack = error.response?.data?.errorStack;
      return rejectWithValue(
        Array.isArray(errorStack) && errorStack.length > 0
          ? errorStack[0].split(' ').slice(1).join(' ') //extract the error message without the field name
          : error.response?.data?.message,
      );
    }
  },
);

export const modifyAutoExclusions = createAsyncThunk(
  'company/modifyAutoExclusions',
  async (autoExclusions: string[], { rejectWithValue }) => {
    try {
      const response = await apiInstance.patch('/organization', {
        thirteenTrusteeAddressExclusions: autoExclusions,
      });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const getDocumentDescriptions = createAsyncThunk(
  'company/getDocumentDescriptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.get('/organization/document-descriptions');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const createDocumentDescription = createAsyncThunk(
  'company/createDocumentDescription',
  async (document: { text: string; orderNum: number }, { rejectWithValue }) => {
    try {
      const response = await apiInstance.post('/organization/document-descriptions', document);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const deleteDocumentDescription = createAsyncThunk(
  'company/deleteDocumentDescription',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiInstance.delete(`/organization/document-descriptions?descriptionId=${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const editDocumentDescription = createAsyncThunk(
  'company/editDocumentDescription',
  async (
    description: { id: number; text?: string; orderNum?: number } | { id: number; text?: string; orderNum?: number }[],
    { rejectWithValue },
  ) => {
    try {
      const response = await apiInstance.patch(`/organization/document-descriptions`, { items: description });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const getPaymentMethods = createAsyncThunk('company/getPaymentMethods', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.get('/payment/methods');

    return response.data.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});

export const attachPaymentMethod = createAsyncThunk(
  'company/attachPaymentMethod',
  async ({ paymentMethodId, isDefault }: { paymentMethodId: string; isDefault?: boolean }, { rejectWithValue }) => {
    try {
      const response = await apiInstance.post('/payment/methods/attach', { paymentMethodId, isDefault });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const editPaymentMethod = createAsyncThunk(
  'company/editPaymentMethod',
  async (
    {
      paymentMethodId,
      isDefault,
      newPaymentMethodId,
    }: { paymentMethodId: string; isDefault?: boolean; newPaymentMethodId?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiInstance.patch('/payment/methods/edit', {
        paymentMethodId,
        isDefault,
        newPaymentMethodId,
      });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const deletePaymentMethod = createAsyncThunk(
  'company/deletePaymentMethod',
  async (paymentMethodId: string, { rejectWithValue }) => {
    try {
      const response = await apiInstance.delete(`/payment/method/${paymentMethodId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const getPaymentMethod = createAsyncThunk(
  'company/getPaymentMethod',
  async (paymentMethodId: string, { rejectWithValue }) => {
    try {
      const response = await apiInstance.get(`/payment/method/${paymentMethodId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const addOrganizationPromoCode = createAsyncThunk(
  'company/addOrganizationPromoCode',
  async (name: string, { rejectWithValue }) => {
    try {
      const response = await apiInstance.post(`/organization/promocodes`, { name });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const getOrganizationPrintCredits = createAsyncThunk(
  'company/getOrganizationPrintCredits',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.get('/organization/print-credits');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const submitPAF = createAsyncThunk('company/submitPAF', async (PAF: PafValues, { rejectWithValue }) => {
  try {
    const response = await apiInstance.post('/organization/paf', PAF);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});

export const getPAF = createAsyncThunk('company/submitPAF', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.get('/organization/paf');
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response.data.message);
  }
});
